import type { CellData, RowData, TableFeatures } from "@tanstack/react-table";
import { z } from "zod";

declare module "@tanstack/react-table" {
  interface ColumnMeta<
    TFeatures extends TableFeatures,
    TData extends RowData,
    TValue extends CellData = CellData,
  > {
    align?: "left" | "center" | "right";
    /**
     * Under `layout="fixed"`, reads this column's `size` as PIXELS instead of as a
     * weight; those pixels come off the table before the rest divide the remainder.
     * For a row-actions column. Ignored under `auto`.
     */
    fixedWidth?: boolean;
    /**
     * The narrowest this column may render, in pixels, under `layout="fixed"`;
     * together the minimums are where the table stops shrinking and the wrapper
     * scrolls. Defaults to what the heading needs (`label` + sort control +
     * padding); set it to widen a column whose values need more, or to narrow one
     * whose label overstates it.
     */
    minWidth?: number;
    /**
     * Marks the column sortable, so its `<th>` exposes `aria-sort`.
     */
    sortable?: boolean;
    /**
     * The column name as a STRING, for the column menu and the default
     * `minWidth`. A `header` may be JSX, so it cannot serve as one.
     */
    label?: string;
  }
}

const SortOrderSchema = z.enum(["asc", "desc"]);

type SortOrder = z.infer<typeof SortOrderSchema>;

export { SortOrderSchema };
export type { SortOrder };
